import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const ambassadors = [
  {
    slug: 'ajay-pathak',
    name: 'Ajay Pathak',
    role: 'UK Co-Chair & Partner at Goodwin',
    image: '/images/ambassadors/ajay-pathak.jpg',
    highlights: [
      "Top 50 Most Influential Lawyers in Europe '25 & '26",
      'Grew Goodwin London from 40 to 200+ lawyers',
      'Trustee, Dharma Endowment Fund',
    ],
    bio: "Ajay Pathak is a partner in Goodwin's Private Equity group and UK Co-Chair, having grown the London office from 40 to over 200 lawyers across London and Cambridge, with recent mandates including a €500M VC fundraise and a €1.8 billion credit fund. Named to Financial News' Fifty Most Influential Lawyers in Europe 2025 and 2026, he also serves as a trustee of the Dharma Endowment Fund - a UK registered charity that funds Dharmic initiatives globally by investing donated capital in Dharmically screened instruments.",
  },
  {
    slug: 'akshay-ruparelia',
    name: 'Akshay Ruparelia',
    role: 'Founder at Doorsteps, AKR, 1C & BrdgeAI',
    image: '/images/ambassadors/akshay-ruparelia.jpg',
    highlights: [
      "UK's youngest millionaire, at 17",
      'Forbes 30 Under 30 Europe',
      'Advisory board, Dharma Endowment Fund',
    ],
    bio: "Akshay Ruparelia founded Doorsteps at 17 while studying for his A-Levels - an online estate agency that became the UK's 10th largest within three years, saving customers £15 million in fees and making him the UK's youngest millionaire and a Forbes 30 Under 30 Europe 2021 honouree. He serves on the advisory board of the Dharma Endowment Fund, the Prince's Trust RISE Campaign, and the Jobs Foundation, and is a member of the Royal Association for Deaf people, having grown up as a carer to his deaf parents.",
  },
  {
    slug: 'anil-agarwal',
    name: 'Anil Agarwal',
    role: 'Founder & Chairman at Vedanta Resources Ltd',
    image: '/images/ambassadors/anil-agarwal.jpg',
    highlights: [
      '$35bn invested across India',
      'Only Indian signatory of The Giving Pledge',
      'Nand Ghar: 8,000+ rural centres, 300,000+ children',
    ],
    bio: "Anil Agarwal founded Vedanta Resources in 1976, growing it from an Indian domestic mining company into one of the world's largest natural resources conglomerates - investing over $35 billion in India and achieving the first-ever primary London Stock Exchange listing by an Indian company. The only Indian signatory of The Giving Pledge in 2021, he has committed 75% of his wealth to philanthropy, with his flagship initiative Nand Ghar transforming 8,000+ rural childcare centres across 15 Indian states, directly impacting 300,000+ children and 200,000+ women through education, nutrition, and healthcare.",
  },
  {
    slug: 'bhupendra-kansagra',
    name: 'Bhupendra Kansagra',
    role: 'Director at Solai Holdings Ltd.',
    image: '/images/ambassadors/bhupendra-kansagra.jpg',
    highlights: [
      "Founded SpiceJet, India's pioneering low-cost airline",
      '50+ years operating across UK and globally',
      'Oil & gas, mining, hospitality, real estate',
    ],
    bio: "Bhupendra Kansagra founded SpiceJet Ltd. in 2005, transforming the defunct ModiLuft into India's pioneering low-cost airline and democratising air travel for millions of Indian passengers before exiting in 2010. He directs Solai Holdings Ltd. and its group of companies operating across the UK and globally for over 50 years, with a portfolio spanning oil and gas, mining, hospitality, manufacturing, shipping and real estate.",
  },
  {
    slug: 'radhi-devlukia',
    name: 'Radhi Devlukia',
    role: 'Entrepreneur, Author & Global Voice on Mindful Living',
    image: '/images/ambassadors/radhi-devlukia.jpg',
    highlights: [
      'NYT & Sunday Times bestselling author of JoyFull',
      'Host of A Really Good Cry podcast',
      '2M+ global community',
    ],
    bio: 'Radhi Devlukia is the New York Times and Sunday Times bestselling author of JoyFull - a cookbook of 125+ plant-based recipes rooted in Ayurvedic principles - host of the A Really Good Cry podcast, and co-founder of Juni Tea, an adaptogenic wellness brand. A qualified clinical dietitian, nutritionist, and Ayurvedic Health Counsellor, she has built a global community of over 2 million followers, and has also led outreach programmes focused on nutrition and welfare for underprivileged children.',
  },
  {
    slug: 'rajeev-srivastava',
    name: 'Rajeev Srivastava',
    role: 'Managing Partner at Capital Square Partners',
    image: '/images/ambassadors/rajeev-srivastava.jpg',
    highlights: [
      'Harvard Advanced Leadership Fellow',
      'Scaled Apar Infotech to $78M EV',
      'Feeds 1.2M schoolchildren daily via Annamrita',
    ],
    bio: "Rajeev Srivastava is the founder of Basil Partners and Managing Partner of Capital Square Partners, Singapore-based private equity firms backing high-growth technology businesses across the US, India, and Southeast Asia - having previously co-founded Apar Infotech Corp, scaling it to a $78M enterprise value. A Harvard Advanced Leadership Fellow, he is co-founder of the Artha Forum, a Vedic-inspired framework for ethical wealth creation, and supports the Annamrita Foundation's mid-day meal programme, feeding 1.2 million schoolchildren daily across India.",
  },
];

const initialsOf = (name) =>
  name
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('');

const AmbassadorPortrait = ({ amb }) => {
  const [errored, setErrored] = useState(false);
  return (
    <div
      className="relative w-full aspect-[4/5] overflow-hidden rounded-3xl shadow-2xl bg-dark-green"
      data-testid={`ambassador-portrait-${amb.slug}`}
    >
      {!errored ? (
        <img
          src={amb.image}
          alt={amb.name}
          className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
          onError={() => setErrored(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-dark-green via-[#0d2820] to-[#1a4233]">
          <span className="font-serif text-cream/40 text-7xl md:text-8xl tracking-widest">
            {initialsOf(amb.name)}
          </span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-dark-green/80 via-dark-green/20 to-transparent pointer-events-none" />
    </div>
  );
};

const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Respect users who prefer reduced motion or older browsers without IO.
    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      typeof IntersectionObserver === 'undefined'
    ) {
      setVisible(true);
      return;
    }
    // Defer observer set-up so the initial opacity-0 paint is committed
    // before any potential synchronous intersection callback flips it on.
    let obs;
    const raf = requestAnimationFrame(() => {
      obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible(true);
            obs.disconnect();
          }
        },
        { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
      );
      obs.observe(node);
    });
    return () => {
      cancelAnimationFrame(raf);
      if (obs) obs.disconnect();
    };
  }, []);
  return [ref, visible];
};

const Spread = ({ amb, reverse }) => {
  const [ref, visible] = useReveal();
  const [expanded, setExpanded] = useState(false);
  return (
    <article
      ref={ref}
      style={{
        transitionDuration: '1100ms',
        transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(56px) scale(0.96)',
        opacity: visible ? 1 : 0,
        transitionProperty: 'opacity, transform',
        willChange: 'opacity, transform',
      }}
      className="grid md:grid-cols-12 gap-8 md:gap-12 items-center"
      data-testid={`ambassador-card-${amb.slug}`}
    >
      {/* Portrait */}
      <div className={`md:col-span-4 ${reverse ? 'md:order-2' : 'md:order-1'}`}>
        <AmbassadorPortrait amb={amb} />
      </div>

      {/* Copy */}
      <div
        className={`md:col-span-8 ${
          reverse ? 'md:order-1 md:pr-6' : 'md:order-2 md:pl-6'
        }`}
      >
        <div className="flex items-center gap-4 mb-4">
          <div className="h-px bg-dark-green/20 flex-1" />
        </div>
        <p className="text-dark-green/70 tracking-[0.2em] uppercase text-xs sm:text-sm font-medium mb-3">
          {amb.role}
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-dark-green mb-5 leading-tight">
          {amb.name}
        </h2>
        {/* Highlight chips - sourced from the bio, visible always */}
        <ul className="flex flex-wrap gap-2 mb-5">
          {amb.highlights.map((h) => (
            <li
              key={h}
              className="text-[11px] sm:text-xs font-mono tracking-tight uppercase text-dark-green/75 border border-dark-green/20 rounded-full px-3 py-1.5"
            >
              {h}
            </li>
          ))}
        </ul>
        <div
          className={`grid transition-all duration-500 ease-out ${
            expanded ? 'grid-rows-[1fr] opacity-100 mb-5' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            <p className="text-dark-green/80 text-base md:text-lg leading-relaxed">
              {amb.bio}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`ambassador-toggle-${amb.slug}`}
          className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-dark-green/70 hover:text-dark-green border-b border-dark-green/30 hover:border-dark-green pb-1 transition-colors"
        >
          {expanded ? 'Hide bio' : 'Read full bio'}
          <span aria-hidden className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}>
            &darr;
          </span>
        </button>
      </div>
    </article>
  );
};

const OurAmbassadors = () => {
  return (
    <div className="min-h-screen bg-cream" data-testid="ambassadors-page">
      {/* Hero */}
      <section className="bg-cream pt-28 sm:pt-32 md:pt-36 pb-12 md:pb-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-dark-green/60 font-mono tracking-[0.25em] text-xs sm:text-sm uppercase mb-5">
            <span className="text-dark-green/40">&rarr;</span> Dharma Initiative &middot; Ambassadors
          </p>
          <h1
            className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-tight tracking-tight mb-8 max-w-6xl"
            data-testid="ambassadors-hero-heading"
          >
            <span className="text-gradient-hero">Our Ambassadors</span>
            <br />
            <span className="text-gradient-hero-light italic">& Speakers.</span>
          </h1>
          <p className="text-dark-green/75 text-lg sm:text-xl md:text-2xl leading-snug max-w-3xl">
            Meet the people who believe in what we're building.
          </p>
        </div>
      </section>

      {/* Editorial alternating list */}
      <section className="bg-cream pb-20 md:pb-28 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto space-y-20 md:space-y-28">
          {ambassadors.map((amb, i) => (
            <Spread key={amb.slug} amb={amb} reverse={i % 2 === 1} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default OurAmbassadors;
